import type { AxiosError } from 'axios';

import type {
  CompanyIntegration,
  IntegrationSettings,
} from '@entities/integration';
import type { IntegrationSettingClient } from '@usecases/store-integration-settings.usecase';

import type { HttpClientOptions } from '@main/http/http-client';
import { HttpClient } from '@main/http/http-client';
import { HttpRequestInterceptors } from '@main/http/http-request-interceptors';
import { idHttpClient } from '@main/http/id-http-client';
import { ScvConfigStorage } from '@main/sf-connector/scv-config-storage';
import type { IntegrationManagerClient } from '@usecases/get-signed-recording-url.usecase';

export class IntegrationManagerHttpClient
  extends HttpClient
  implements IntegrationSettingClient, IntegrationManagerClient
{
  private static host = process.env.HOST;
  private static baseURl = `https://integrations-manager.${this.host}`;

  private static options: HttpClientOptions = {
    baseUrl: this.baseURl,
    retryConfig: {
      preRetryOp: this.preRetryOp,
      retryCondition: this.retryCondition,
    },
  };

  constructor() {
    super(IntegrationManagerHttpClient.options);
    this.axiosInstance.interceptors.request.use(
      HttpRequestInterceptors.addBearerTokenInterceptor,
    );
  }

  private static async preRetryOp(error: AxiosError) {
    const { response } = error;
    if (response && (response.status === 403 || response.status == 401)) {
      return idHttpClient.updateToken();
    }
  }

  private static retryCondition(error: AxiosError, retryCount: number) {
    const { message } = error;
    const { response } = error;

    const isNetworkError =
      message?.toLowerCase().includes('timeout') ||
      message?.toLowerCase().includes('network error');

    const isUnAuthenticated =
      (response?.status === 403 || response?.status == 401) && retryCount < 1;

    return isUnAuthenticated || isNetworkError;
  }

  async fetchIntegrationSettings(
    integrations: CompanyIntegration[],
    companyId: number,
  ): Promise<IntegrationSettings[]> {
    const integrationIds = [
      ...new Set(integrations.map(({ id }: CompanyIntegration) => id)),
    ];

    const params = {
      integrationIds,
      companyId,
    };

    const settings = await this.post<IntegrationSettings[]>(
      '/v1/integrations/cti_dashboard_settings',
      params,
    );

    /**
     * Return only those settings belongs current salesforce org
     * TODO: check with salesforce developer for proper org url.
     * Work around is to remove '-scrt' from scrt url
     */
    const salesforceOrgUrl: string = ScvConfigStorage.get().scrtUrl.replace(
      '-scrt',
      '',
    );

    return settings.filter(
      ({ instanceUrl }: IntegrationSettings) =>
        instanceUrl === salesforceOrgUrl,
    );
  }

  async getrecordingUrlByCallId(recordingUrl: string): Promise<string> {
    try {
      const res = await this.get<string>(`${recordingUrl}`);

      return res;
    } catch (e) {
      console.log(e);
      return '';
    }
  }
}

export const integrationManagerHttpClient = new IntegrationManagerHttpClient();
